import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Store} from '@ngrx/store';
import {AppState} from '@store/state/app.state';
import {EnvironmentService} from '@iw-shop/core//services/environment.service';
import {Observable} from 'rxjs';
import {
    ApiCustomersGetRequest,
    ApiCustomersGetResponse, ApiCustomersSetRequest, ApiCustomersSetResponse,
    ApiCustomersUpdateRequest,
    ApiCustomersUpdateResponse
} from '@api/customers';
import {CustomerArticleStocks} from '@iw-shop/customer/interfaces/customer';
import {ApiCustomerDocumentsGetRequest, ApiCustomerDocumentsGetResponse} from '@api/customer-documents';
import {take} from "rxjs/operators";

@Injectable({
    providedIn: 'root'
})
export class CustomerService {

    customerId: string = '';

    constructor(private http: HttpClient, private store: Store<AppState>, private environmentService: EnvironmentService) {
    }

    set(data: ApiCustomersSetRequest['params']['data']): Observable<ApiCustomersSetResponse> {
        const body: ApiCustomersSetRequest = {
            id: 'setCustomer#INVALID_PARAMS',
            command: 'customers/set',
            params: {
                data
            }
        };
        return this.http.post<ApiCustomersSetResponse>(this.environmentService.environment.urlApi, JSON.stringify(body));
    }

    update(data: ApiCustomersUpdateRequest['params']['data']): Observable<ApiCustomersUpdateResponse> {
        const body: ApiCustomersUpdateRequest = {
            id: 'updateCustomer#INVALID_PARAMS',
            command: 'customers/update',
            params: {
                data
            }
        };
        return this.http.post<ApiCustomersUpdateResponse>(this.environmentService.environment.urlApi, JSON.stringify(body));
    }

    hasAnyRequiredFields(): boolean {
        let anyRequired: boolean = false;

        this.store.select(state => state.config)
            .pipe(take(1))
            .subscribe(c => {
                if (c.customerShopFields) {
                    // .some() gibt true zurück, sobald ein Element die Bedingung erfüllt
                    anyRequired = c.customerShopFields.some(r => r.required === true);
                }
            });

        return anyRequired;
    }

    isCustomerShopFieldRequired(field: string): boolean {
        let isRequired: boolean = false;
        this.store.select(state => state.config)
            .pipe(
                take(1)
            )
            .subscribe(
                c => {
                    if (c.customerShopFields) {
                        c.customerShopFields.map(r => {
                            if (r.field == field &&
                                r.required) {
                                isRequired = true;
                            }
                        });
                    }
                }
            );
        return isRequired;
    }

    getCustomerShopFieldErrorMsg(field: string, defaultErrorMsg?: string) {
        let errorMsg: string = defaultErrorMsg ?? '';

        this.store.select(state => state.config)
            .pipe(
                take(1)
            )
            .subscribe(

                c => {
                    if (c.customerShopFields) {
                        c.customerShopFields.map(r => {
                            if (r.field == field &&
                                r.errorMsg) {
                                errorMsg = r.errorMsg;
                            }
                        });
                    }
                }
            );
        return errorMsg;
    }

    isCustomerShopFieldHidden(field: string): boolean {
        let isHidden: boolean = false;
        this.store.select(state => state.config)
            .pipe(
                take(1)
            )
            .subscribe(
                c => {
                    if (c.customerShopFields) {
                        c.customerShopFields.map(r => {
                            if (r.field == field &&
                                r.hidden) {
                                isHidden = true;
                            }
                        });
                    }
                }
            );
        return isHidden;
    }

    isCustomerShopFieldDisabled(field: string): boolean {
        let isDisabled: boolean = false;
        this.store.select(state => state.config)
            .pipe(
                take(1)
            )
            .subscribe(
                c => {
                    if (c.customerShopFields) {
                        c.customerShopFields.map(r => {
                            if (r.field == field &&
                                r.disabled) {
                                isDisabled = true;
                            }
                        });
                    }
                }
            );
        return isDisabled;
    }

    downloadDocument(id: string, customerNumber: string) {
        const body: ApiCustomerDocumentsGetRequest = {
            id: 'downloadDocument',
            command: 'customerDocuments/get',
            params: {
                filters: [
                    {fields: ['customerNumber'], values: [customerNumber]},
                    {fields: ['id'], values: [id]},
                    {fields: ['type'], values: ['invoice']}
                ],
                customerDocuments: {
                    start: 0,
                    limit: 1,
                    expectedData: {
                        fileBase64: 1
                    }
                }
            }
        };
        this.http.post<ApiCustomerDocumentsGetResponse>(this.environmentService.environment.urlApi, JSON.stringify(body))
            .subscribe(
                r => {
                    if (r.result?.customerDocuments.data[0].fileBase64) {
                        const byteArray = new Uint8Array(atob(r.result?.customerDocuments.data[0].fileBase64).split('').map(char => char.charCodeAt(0)));
                        const file = new Blob([byteArray], {type: 'application/pdf'});
                        const fileURL = URL.createObjectURL(file);
                        window.open(fileURL);
                    }
                }
            )

    }

    getDocuments(customerNumber: string, types: string[], start: number, limit: number): Observable<ApiCustomerDocumentsGetResponse> {
        const body: ApiCustomerDocumentsGetRequest = {
            id: 'customerGetDocuments',
            command: 'customerDocuments/get',
            params: {
                filters: [
                    {fields: ['customerNumber'], values: [customerNumber]},
                    {fields: ['type'], values: types}
                ],
                customerDocuments: {
                    start,
                    limit,
                    sort: [
                        {field: 'date', direction: -1}
                    ],
                    expectedData: {
                        id: 1,
                        amount: 1,
                        date: 1,
                        uniqueNumber: 1,
                        openAmount: 1
                    }
                }
            }
        };
        return this.http.post<ApiCustomerDocumentsGetRequest>(this.environmentService.environment.urlApi, JSON.stringify(body));
    }

    updateFavorites(customerId: string, favoritesList: string[] | []): Observable<ApiCustomersUpdateResponse> {
        const body: ApiCustomersUpdateRequest = {
            id: 'updateCustomerFavorites',
            command: 'customers/update',
            params: {
                data: {
                    id: customerId,
                    favoritesList
                }
            }
        };
        return this.http.post<ApiCustomersUpdateRequest>(this.environmentService.environment.urlApi, JSON.stringify(body));
    }

    updateArticleStocks(customerId: string, articleStocks: CustomerArticleStocks[] | []): Observable<ApiCustomersUpdateResponse> {
        const body: ApiCustomersUpdateRequest = {
            id: 'updateArticleStocks',
            command: 'customers/update',
            params: {
                data: {
                    id: customerId,
                    articleStocks
                }
            }
        };
        return this.http.post<ApiCustomersUpdateRequest>(this.environmentService.environment.urlApi, JSON.stringify(body));
    }

    getCustomers(customerIds: string[]): Observable<ApiCustomersGetResponse> {
        const body: ApiCustomersGetRequest = {
            id: 'getCustomer',
            command: 'customers/get',
            params: {
                filters: [
                    {
                        fields: ['id'],
                        values: customerIds
                    }
                ],
                customers: {
                    expectedData: {
                        id: 1,
                        customerNumber: 1,
                        status: 1,
                        email: 1,
                        priceGroup: 1,
                        priceMode: 1,
                        addresses: 1,
                        numberOfRepresentative: 1,
                        CEO: 1,
                        buyer: 1,
                        customerSince: 1,
                        classification: 1,
                        defaultDeliveryPeriod: 1,
                        dateOfBirth: 1,
                        creditLimit: 1,
                        creditLimitAvailable: 1,
                        favoritesList: 1,
                        articleStocks: 1,
                        articlesInDelivery: 1,
                        visibilityGroups: 1,
                        lastUpdate: 1,
                        dynamicArticleIds: 1
                    }
                }
            }
        };
        return this.http.post<ApiCustomersGetResponse>(this.environmentService.environment.urlApi, JSON.stringify(body), {withCredentials: true});
    }
}
